import React, { useState } from "react";
import { FaArrowLeft, FaCheckCircle } from "react-icons/fa";

// Shared "Step 2" for every caretaker requirement form (Baby, Elder,
// Patient, Home Nursing, General, Pet Care): review + accept Terms &
// Conditions, then Submit. The terms text below is a placeholder written
// in-house — it will be replaced with the finalised legal copy once ready.
// requestBody.terms_accepted is set to true by the parent's submit handler
// only once this checkbox has actually been ticked (see each page's
// handleSubmit — it can only be reached from here).
const TermsAcceptStep = ({ onBack, onSubmit, isSubmitting, submitError, serviceLabel = "this service" }) => {
  const [agreed, setAgreed] = useState(false);

  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (!agreed) return;
    onSubmit();
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h2 style={S.title}>Terms &amp; Conditions</h2>
        <p style={S.sub}>
          Please review before submitting your requirement for {serviceLabel}.
        </p>
      </div>

      <div style={S.termsBox}>
        <p style={S.placeholderNote}>
          (Placeholder terms — the finalised Terms &amp; Conditions for this service
          will replace this text.)
        </p>
        <ol style={S.termsList}>
          <li>ClickNido connects you with an independent, verified caretaker professional — the caretaker is not a direct employee of ClickNido.</li>
          <li>The details you provide in this requirement form are shared with the assigned caretaker to help them prepare for the job.</li>
          <li>Salary/rate offered is a starting point for discussion and may be confirmed directly with the assigned caretaker.</li>
          <li>You're responsible for providing accurate information about the care recipient's needs and any relevant medical or safety details.</li>
          <li>Cancelling or rescheduling a confirmed caretaker booking may be subject to ClickNido's Cancellation Policy.</li>
          <li>ClickNido may contact you to verify details or follow up on this requirement.</li>
        </ol>

        {/* Shared across every caretaker requirement form that uses this
            step (Baby, Elder, Patient, Home Nursing, General) — Pet Care
            doesn't use TermsAcceptStep, so it's unaffected. */}
        <div style={S.policySection}>
          <h4 style={S.policyHeading}>5. Caretaker Protection</h4>
          <p style={S.policyIntro}>
            Applies to approved Baby Caretaker, Child Caretaker, Elder Care, Patient Home Nurse, General Caretaker and Medical Representative services where applicable.
          </p>

          <h5 style={S.policySubheading}>5.1 Customer Claim Entry</h5>
          <ul style={S.policyList}>
            <li>Open Help &amp; Support and select Caretaker Protection / Caretaker Damage Protection.</li>
            <li>Upload Booking ID.</li>
            <li>Enter a mandatory Note explaining the issue.</li>
            <li>Upload photo/video where applicable.</li>
            <li>Provide bank account details where a monetary protection claim is requested.</li>
            <li>Call Customer Support.</li>
          </ul>

          <h5 style={S.policySubheading}>5.2 Small Negligence / Service Inconvenience</h5>
          <ul style={S.policyList}>
            <li>If a small negligence or service lapse by the caretaker causes inconvenience to the customer or care recipient, the customer must raise the issue during the service period and on the same day.</li>
            <li>ClickNido reviews the booking, caretaker records and available evidence and takes appropriate corrective action.</li>
          </ul>

          <h5 style={S.policySubheading}>5.3 Serious Incident / Serious Misconduct</h5>
          <ul style={S.policyList}>
            <li>If serious misconduct or a serious incident by the caretaker is established, ClickNido may take serious action in accordance with applicable company/partner policy and law.</li>
            <li>Where applicable, financial responsibility or recovery may be handled through caretaker remuneration and/or applicable insurance, subject to relevant policy, insurance terms and law.</li>
          </ul>

          <h5 style={S.policySubheading}>5.4 Caretaker Product / Property Damage Protection — Up to ₹5,000</h5>
          <ul style={S.policyList}>
            <li>Eligible product/property damage caused by a ClickNido caretaker during the booked service may qualify after verification and investigation.</li>
            <li>Customer must raise the damage complaint during the service period and before submitting/confirming Work Complete.</li>
            <li>Customer must provide Booking ID and supporting evidence requested by ClickNido.</li>
            <li>ClickNido officials verify and investigate and determine caretaker responsibility.</li>
            <li>Protection is paid only after caretaker responsibility is established.</li>
            <li>If approved, the eligible protection amount is transferred within 3–5 working days after approval.</li>
          </ul>

          <h5 style={S.policySubheading}>5.5 Customer Information Responsibility</h5>
          <ul style={S.policyList}>
            <li>Customer must provide accurate and complete information about the care recipient and booking.</li>
            <li>Relevant information may include care instructions, routine information, known requirements, allergies/risks where applicable, emergency contacts and other information requested by ClickNido.</li>
            <li>Incorrect, incomplete or missing information can create a poor or unsafe experience for a baby, child, elder, patient, general care recipient or medical representative.</li>
            <li>If a problem occurs because of incorrect, incomplete or insufficient customer information, the related inconvenience, damage, loss or incident may not be eligible for ClickNido coverage or compensation.</li>
          </ul>

          <h4 style={S.policyHeading}>6. Customer Complaint &amp; Evidence Requirement</h4>
          <p style={S.policyIntro}>
            Customers must make complaints truthfully and in good faith. To claim caretaker protection or household damage protection, the customer must provide sufficient information/evidence to support the allegation.
          </p>
          <p style={S.policyIntro}>
            ClickNido may verify booking records, service timestamps, partner/caretaker records, customer notes, photos/videos, service completion status and other relevant evidence before approving or rejecting a claim.
          </p>
        </div>
      </div>

      <label style={S.checkboxRow}>
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          style={S.checkbox}
        />
        <span>I have read and agree to the Terms &amp; Conditions.</span>
      </label>

      {submitError && <p style={S.error}>{submitError}</p>}

      <div style={S.actions}>
        <button type="button" style={S.backBtn} onClick={onBack} disabled={isSubmitting}>
          <FaArrowLeft /> Back
        </button>
        <button
          type="button"
          style={{ ...S.submitBtn, ...(agreed ? {} : S.submitBtnDisabled) }}
          onClick={handleSubmitClick}
          disabled={!agreed || isSubmitting}
        >
          <FaCheckCircle /> {isSubmitting ? "Submitting…" : "Submit Requirement"}
        </button>
      </div>
    </div>
  );
};

const S = {
  wrap: { padding: "28px", maxWidth: "720px", margin: "0 auto" },
  header: { marginBottom: "18px" },
  title: { margin: "0 0 6px", fontSize: "22px", fontWeight: 800, color: "#111827" },
  sub: { margin: 0, fontSize: "14px", color: "#6b7280" },
  termsBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "20px",
    maxHeight: "320px",
    overflowY: "auto",
    marginBottom: "18px",
  },
  placeholderNote: {
    margin: "0 0 12px",
    fontSize: "12.5px",
    fontStyle: "italic",
    color: "#9ca3af",
  },
  termsList: {
    margin: 0,
    paddingLeft: "20px",
    fontSize: "14px",
    lineHeight: 1.9,
    color: "#374151",
  },
  policySection: {
    marginTop: "18px",
    paddingTop: "16px",
    borderTop: "1px solid #e2e8f0",
  },
  policyHeading: {
    margin: "14px 0 6px",
    fontSize: "15px",
    fontWeight: 800,
    color: "#111827",
  },
  policySubheading: {
    margin: "12px 0 4px",
    fontSize: "13.5px",
    fontWeight: 700,
    color: "#1d4ed8",
  },
  policyIntro: {
    margin: "0 0 6px",
    fontSize: "13.5px",
    lineHeight: 1.7,
    color: "#374151",
  },
  policyList: {
    margin: 0,
    paddingLeft: "20px",
    fontSize: "13.5px",
    lineHeight: 1.8,
    color: "#374151",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "14px",
    color: "#111827",
    fontWeight: 600,
    marginBottom: "10px",
    cursor: "pointer",
  },
  checkbox: { width: "18px", height: "18px", marginTop: "2px", cursor: "pointer" },
  error: { color: "#dc2626", fontSize: "13px", fontWeight: 600, margin: "10px 0 0" },
  actions: { display: "flex", gap: "12px", marginTop: "22px" },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "13px 20px",
    borderRadius: "12px",
    border: "1px solid #d5dbe3",
    background: "#fff",
    color: "#374151",
    fontWeight: 700,
    fontSize: "14px",
    cursor: "pointer",
  },
  submitBtn: {
    flex: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "13px 20px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg,#4f46e5,#4338ca)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "14px",
    cursor: "pointer",
  },
  submitBtnDisabled: {
    background: "#c7cbe0",
    cursor: "not-allowed",
  },
};

export default TermsAcceptStep;
